// Lib
import React, { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { debounce } from 'lodash';

// Utils
import zoomStateSingleton from '../zoomStateSingleton';
import { getZoomTextInUnits } from '../zoomDisplayUtils';

// Selectors
import { canvasZoomScaleSelector } from '../../store/canvasZoomSelector';
import { getCurrentUserQuickNotesRootBoardId } from '../../../user/currentUserSelector';
import { getCurrentBoardId } from '../../../reducers/currentBoardId/currentBoardIdSelector';

// Actions
import { setCanvasZoomTargetScale } from '../../store/canvasActions';

// Components
import Toast from '../../../structural/toast/Toast';
import AnimationLock from '../../../components/animations/AnimationLock';

// Styles
import './ZoomToast.scss';

const ZoomToastContents: React.FC = () => {
    const elementRef = React.useRef<HTMLDivElement>(null);
    const canvasZoomScale = useSelector(canvasZoomScaleSelector);

    const dispatch = useDispatch();
    const dispatchSetCanvasZoomTargetScale = (scale: number) => dispatch(setCanvasZoomTargetScale(scale));

    const resetZoom = useCallback(() => dispatchSetCanvasZoomTargetScale(1), []);

    useEffect(() => {
        const onZoomChange = (zoom: number) => {
            if (!elementRef.current) return;

            elementRef.current.innerText = getZoomTextInUnits(zoom * 100);
        };

        zoomStateSingleton.registerZoomSubscriber(onZoomChange);

        return () => {
            zoomStateSingleton.unregisterZoomSubscriber(onZoomChange);
        };
    }, []);

    return (
        <AnimationLock animating={canvasZoomScale === 1}>
            <div ref={elementRef} className="ZoomToastContents" onClick={resetZoom}>
                {getZoomTextInUnits(canvasZoomScale * 100)}
            </div>
        </AnimationLock>
    );
};

const ZoomToast: React.FC = () => {
    const currentBoardId = useSelector(getCurrentBoardId);
    const quickNotesBoardId = useSelector(getCurrentUserQuickNotesRootBoardId);

    const zoomScale = useSelector(canvasZoomScaleSelector);
    const [showToast, setShowToast] = React.useState(false);

    /**
     * Hides the toast after a brief period of inactivity.
     */
    const debouncedHideToast = useCallback(
        debounce(() => setShowToast(false), 1000),
        [],
    );

    /**
     * Shows the toast for a brief moment, then hides it.
     */
    const momentarilyShowToast = useCallback(() => {
        setShowToast(true);
        debouncedHideToast();
    }, []);

    // When changing boards - immediately hide the toast if the new board's zoom is 1 or
    //  if we're switching to the quick notes board
    //  Otherwise, show the toast briefly
    useEffect(() => {
        const shouldShowToast = currentBoardId !== quickNotesBoardId && zoomScale !== 1;
        shouldShowToast ? momentarilyShowToast() : setShowToast(false);
    }, [currentBoardId]);

    // We need the zoom to show immediately if it's not 1, so we need to use state and
    //  the singleton subscriber to achieve this, because redux doesn't update immediately
    useEffect(() => {
        const onZoomChange = (zoom: number) => (zoom !== 1 ? momentarilyShowToast() : debouncedHideToast());

        zoomStateSingleton.registerZoomSubscriber(onZoomChange);

        return () => {
            zoomStateSingleton.unregisterZoomSubscriber(onZoomChange);
            debouncedHideToast.cancel();
        };
    }, []);

    if (!showToast) return null;

    return (
        <Toast className="ZoomToast">
            <ZoomToastContents />
        </Toast>
    );
};

export default ZoomToast;
